import { useState } from "react";
import { motion } from "framer-motion";
import { Award, QrCode, ExternalLink, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { TEST_IDS } from "@/lib/testIds";

const certificates = [
  {
    title: "Professional Diploma in Sports Nutrition",
    org: "FC Barcelona Innovation Hub",
    date: "17 Mayıs 2026",
    id: "OKsSUsEdt1",
    image: "/assets/cert1.png",
  },
  {
    title: "Certificate in Sports Nutrition for Football",
    org: "FC Barcelona Innovation Hub",
    date: "20 Mayıs 2026",
    id: "6tg8QBWYbK",
    image: "/assets/cert2.png",
  },
];

export const Certificates = () => {
  const [active, setActive] = useState(null);

  return (
    <section
      id="sertifikalar"
      className="relative bg-white py-24 md:py-32 border-t border-[#0F2D2A]/8 overflow-hidden"
    >
      {/* soft gradient strip on left */}
      <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-[#10B981] via-[#0AB7C5] to-[#0EA5E9]" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6 }}
          >
            <p className="label-eyebrow mb-4">02 — Sertifikalar</p>
            <h2 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#0F2D2A] leading-[1.02]">
              FC Barcelona <br />
              Innovation Hub{" "}
              <span className="bg-gradient-to-r from-[#10B981] to-[#0EA5E9] bg-clip-text text-transparent">
                Diplomalar
              </span>
            </h2>
          </motion.div>
          <p className="text-base text-[#1E4842] max-w-md">
            Uluslararası kabul gören iki diploma. Her ikisi de Barça Innovation
            Hub QR kodu ile bağımsız olarak doğrulanabilir.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {certificates.map((c, i) => (
            <motion.article
              key={c.id}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              whileHover={{ y: -8 }}
              className="group relative rounded-3xl bg-white border border-[#0F2D2A]/8 overflow-hidden shadow-[0_20px_50px_-30px_rgba(15,45,42,0.2)] hover:shadow-[0_30px_70px_-30px_rgba(14,165,233,0.4)] transition-shadow duration-500"
            >
              <div className="relative aspect-[4/3] overflow-hidden bg-white border-b border-[#0F2D2A]/8">
                <img
                  src={c.image}
                  alt={c.title}
                  className="absolute inset-0 w-full h-full object-contain p-4 transition-transform duration-700 group-hover:scale-[1.03]"
                  loading="lazy"
                />
                <div className="absolute top-5 left-5 inline-flex items-center gap-2 bg-gradient-to-r from-[#10B981] to-[#0EA5E9] text-white px-3 py-1.5 rounded-full">
                  <Award className="w-3.5 h-3.5" />
                  <span className="text-[10px] font-extrabold uppercase tracking-[0.18em]">
                    {c.date}
                  </span>
                </div>
              </div>

              <div className="p-6 md:p-7 space-y-5">
                <div>
                  <p className="text-[10px] font-extrabold uppercase tracking-[0.22em] bg-gradient-to-r from-[#10B981] to-[#0EA5E9] bg-clip-text text-transparent">
                    {c.org}
                  </p>
                  <h3 className="mt-2 font-heading font-black text-[#0F2D2A] text-xl md:text-2xl leading-tight">
                    {c.title}
                  </h3>
                </div>
                <div className="flex items-end justify-between gap-4">
                  <div>
                    <p className="text-[10px] font-extrabold uppercase tracking-[0.2em] text-[#1E4842]/70">
                      Sertifika ID
                    </p>
                    <p className="font-mono text-sm md:text-base text-[#0F2D2A] mt-1 tracking-wide">
                      {c.id}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setActive(i)}
                    data-testid={TEST_IDS.certificates.viewBtn(i)}
                    className="btn-shimmer inline-flex items-center gap-2 bg-gradient-to-r from-[#10B981] to-[#0EA5E9] text-white px-5 py-3 rounded-full text-xs font-extrabold uppercase tracking-[0.16em] hover:-translate-y-0.5 hover:shadow-[0_18px_40px_-18px_rgba(14,165,233,0.55)] transition-all duration-300"
                  >
                    Görüntüle
                    <ExternalLink className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </motion.article>
          ))}
        </div>

        <div className="mt-10 inline-flex items-center gap-3 text-sm text-[#1E4842]">
          <span className="inline-flex w-8 h-8 items-center justify-center rounded-full bg-gradient-to-br from-[#10B981] to-[#0EA5E9] text-white">
            <QrCode className="w-4 h-4" />
          </span>
          Sertifikaların doğruluğu Barça Innovation Hub QR kodu ile teyit
          edilebilir.
        </div>
      </div>

      <Dialog open={active !== null} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent
          className="max-w-4xl bg-[#0F2D2A] border-[#0F2D2A] text-white rounded-3xl p-0 overflow-hidden"
          data-testid="certificate-lightbox"
        >
          <DialogHeader className="p-6 flex flex-row items-start justify-between gap-4">
            <div>
              <DialogTitle className="font-heading text-xl md:text-2xl text-white">
                {active !== null && certificates[active].title}
              </DialogTitle>
              <DialogDescription className="text-white/70 mt-1">
                {active !== null &&
                  `${certificates[active].org} · ${certificates[active].date} · ID: ${certificates[active].id}`}
              </DialogDescription>
            </div>
            <button
              type="button"
              onClick={() => setActive(null)}
              data-testid={TEST_IDS.certificates.lightboxClose}
              className="inline-flex w-10 h-10 items-center justify-center rounded-full border border-white/20 hover:bg-white hover:text-[#0F2D2A] transition-colors"
              aria-label="Kapat"
            >
              <X className="w-4 h-4" />
            </button>
          </DialogHeader>
          {active !== null && (
            <div className="bg-white w-full overflow-hidden flex items-center justify-center p-4">
              <img
                src={certificates[active].image}
                alt={certificates[active].title}
                className="max-w-full max-h-[70vh] object-contain"
              />
            </div>
          )}
          <div className="px-6 py-4 text-[11px] uppercase tracking-[0.22em] text-white/60 border-t border-white/10">
            Yüksek çözünürlüklü PNG · Doğrulama: Barça Innovation Hub QR
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default Certificates;
